import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';

// Create a proper React component for the countdown display
const CountdownDisplay = ({ hours, minutes, seconds, completed }) => {
    const [visible, setVisible] = useState(true);
    const isLessThanTwoMinutes = hours === 0 && minutes < 2;
    const [messageShown, setMessageShown] = useState(false);
    const mode = useSelector(store => store.userInfo.mode);
    const messageIdRef = useRef(null);
    const lastTimeRef = useRef({ hours, minutes, seconds });
    
    // Reset messageShown if the countdown is restarted
    useEffect(() => {
        // If time increases compared to last recorded time, the countdown was likely reset
        if (hours > lastTimeRef.current.hours || 
            (hours === lastTimeRef.current.hours && minutes > lastTimeRef.current.minutes) ||
            (hours === lastTimeRef.current.hours && minutes === lastTimeRef.current.minutes && 
             seconds > lastTimeRef.current.seconds)) {
            setMessageShown(false);
        }
        
        // Update last time values
        lastTimeRef.current = { hours, minutes, seconds };
    }, [hours, minutes, seconds]);

    // Handle blinking effect when less than two minutes
    useEffect(() => {
        let interval;
        
        if (isLessThanTwoMinutes && !completed) {
            // Always start visible when entering blinking state
            setVisible(true);
            
            interval = setInterval(() => {
                setVisible(prev => !prev);
            }, 500); // Flash every 500ms
        } else if (!isLessThanTwoMinutes) {
            // Ensure visible when not in blinking state
            setVisible(true);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isLessThanTwoMinutes, completed, hours, minutes, seconds]);

    // Show global message only once when completed
    useEffect(() => {
        if (completed && !messageShown) {
            if (mode !== "I") {
                const id = displayGlobalMessage(
                    <div style={{
                        textAlign: 'center',
                        padding: '10px',
                        animation: 'fadeIn 0.5s'
                    }}>
                        <h1 style={{
                            color: '#e53935',
                            fontSize: '24px',
                            margin: '0 0 10px 0'
                        }}>
                            Time's Up!
                        </h1>
                        <p style={{
                            color: '#555',
                            margin: 0
                        }}>
                            Your allocated time has ended
                        </p>
                    </div>,
                    'info',
                    false,
                    { autoClose: false }
                );
                messageIdRef.current = id;
            }
            setMessageShown(true);
        }
        
        // Cleanup function to close the message when component unmounts
        return () => {
            if (messageIdRef.current && typeof window !== 'undefined') {
                // Assuming there's a close function for the message
                // If not provided by your displayGlobalMessage utility, 
                // you may need to adapt this part
                if (window.closeGlobalMessage) {
                    window.closeGlobalMessage(messageIdRef.current);
                }
            }
        };
    }, [completed, messageShown, mode]);

    if (completed) {
        return (
            <span style={{
                color: '#e53935',
                fontWeight: 'bold',
                fontSize: '1.1em'
            }}>
                Time's Up
            </span>
        );
    }

    // Format the time display in hh:mm:ss format
    const formattedHours = `${hours < 10 ? `0${hours}` : hours}`;
    const formattedMinutes = `${minutes < 10 ? `0${minutes}` : minutes}`;
    const formattedSeconds = `${seconds < 10 ? `0${seconds}` : seconds}`;
    const formattedTime = `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;

    // Determine the style based on time remaining
    const style = {
        color: isLessThanTwoMinutes ? (visible ? 'red' : 'transparent') : 'inherit',
        fontWeight: isLessThanTwoMinutes ? 'bold' : 'normal',
        transition: isLessThanTwoMinutes ? 'none' : 'color 0.2s ease',
    };

    return <span style={style}>{formattedTime}</span>;
};

// Add a CSS keyframe for fading in
if (typeof document !== 'undefined' && !document.getElementById('countdown-styles')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'countdown-styles';
    styleElement.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;
    document.head.appendChild(styleElement);
}

// Renderer function that uses the component
const countdownRenderer = props => {
    return <CountdownDisplay {...props} />;
};

export default countdownRenderer;